import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  uploadCsvCandidateFile,
  uploadPdfCandidateFile,
  uploadSingleCandidateResume,
  updateCandidateDetails,
  clearCandidateList,
  bulkUpdateHiringStatus,
  fetchCandidateProfile,
  fetchAdvancedFilterConfig,
  fetchFilterSubitems,
  searchCandidatesAdvanced,
  exportCandidatesAdvanced,
  fetchSavedSearches,
  createSavedSearch,
  deleteSavedSearch,
} from "../Redux/ActionCreators/candidateAction";
import { assignedStage, fetchJobById } from "../Redux/ActionCreators/jobsAction";
import { postCandidateComment } from "../Redux/ActionCreators/chatAction";
import { getHiringStatuses } from "../Redux/ActionCreators/settingAction";
import { HIRING_STATUS_IDS, SORT_ROWS } from "./candidatesV2Constants";

const ACTIVATE_FILTER_TO_STATUS_ID = {
  shortlisted: HIRING_STATUS_IDS["Short Listed"],
  hired: HIRING_STATUS_IDS["Hired"],
};

// Only keys whose backend payload name differs from the filter's `key` need an entry here.
// Any other "checkbox_list" filter (current or added later) is sent as payload[key] = values.
const CHECKBOX_LIST_PAYLOAD_KEY_MAP = {
  location: "locations",
  hiring_status: "hiring_status_ids",
};

const HIRING_STATUS_ID_TO_LABEL = Object.fromEntries(
  Object.entries(HIRING_STATUS_IDS).map(([label, id]) => [id, label])
);

const useCandidatesV2 = () => {
  const { jobId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();

  const SESSION_PAGE_SIZE_KEY = useMemo(() => `candidates_v2_pageSize_${jobId}`, [jobId]);
  const SESSION_CURRENT_PAGE_KEY = useMemo(() => `candidates_v2_currentPage_${jobId}`, [jobId]);
  const SESSION_SEARCH_TERM_KEY = useMemo(() => `candidates_v2_searchTerm_${jobId}`, [jobId]);

  // Advanced filters handed over by a link (e.g. Job Insights): route state
  // { filterValues: { hiring_status: [id], ... } }. The page then starts clean on page 1
  // with just these filters; their accordions are opened once the filter config loads.
  const handedFiltersRef = useRef(location.state?.filterValues || null);

  // ── Core UI state ──────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState(() =>
    handedFiltersRef.current ? "" : sessionStorage.getItem(SESSION_SEARCH_TERM_KEY) || "");
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [file, setFile] = useState({ type: "", data: [] });
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackSeverity, setSnackSeverity] = useState("error");
  const [snackMessage, setSnackMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [jobLoading, setJobLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [assignProgress, setAssignProgress] = useState({ current: 0, total: 0 });
  const [editModal, setEditModal] = useState({ open: false, candidate: null });
  const [editForm, setEditForm] = useState({ name: "", email: "", phone_number: "" });
  const [resumeModal, setResumeModal] = useState({ open: false, candidateId: null });
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [sortOptions, setSortOptions] = useState([]);
  const [skillsMatchMode, setSkillsMatchMode] = useState("all");
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [filtersChanged, setFiltersChanged] = useState(false);

  const safeGet = (k, fallback) => {
    try {
      const v = Number(window?.sessionStorage?.getItem(k));
      return Number.isFinite(v) && v > 0 ? v : fallback;
    } catch { return fallback; }
  };
  const [pageSize, setPageSize] = useState(() => safeGet(SESSION_PAGE_SIZE_KEY, 10));
  const [currentPage, setCurrentPage] = useState(() => (handedFiltersRef.current ? 1 : safeGet(SESSION_CURRENT_PAGE_KEY, 1)));

  // ── Advanced filter state ──────────────────────────────────────────────────
  const [filterItems, setFilterItems] = useState([]);
  const [openAccordions, setOpenAccordions] = useState(new Set());
  const [subitemsData, setSubitemsData] = useState({});
  const [loadingAccordion, setLoadingAccordion] = useState({});
  const [loadingMoreMap, setLoadingMoreMap] = useState({});
  const [subitemsSearchTerms, setSubitemsSearchTerms] = useState({});
  const [filterValues, setFilterValues] = useState(() => {
    if (handedFiltersRef.current) return { ...handedFiltersRef.current };
    const statusId = ACTIVATE_FILTER_TO_STATUS_ID[location.state?.activateFilter];
    return statusId ? { hiring_status: [statusId] } : {};
  });
  const [filterConfigLoaded, setFilterConfigLoaded] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  // ── Save search modal state ────────────────────────────────────────────────
  const [saveSearchModal, setSaveSearchModal] = useState({ open: false, name: "" });
  const [savedSearchesModal, setSavedSearchesModal] = useState({ open: false, list: [], listLoading: false });

  // ── Refs ───────────────────────────────────────────────────────────────────
  const csvInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const resumeInputRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const exportDropdownPortalRef = useRef(null);
  const subitemsLoadingRef = useRef({});

  // ── Redux selectors ────────────────────────────────────────────────────────
  const latestJob = useSelector((state) => state.jobs.latestJob);
  const candidateList = useSelector((state) => state.candidates.candidateList || []);
  const pagination = useSelector((state) => state.candidates.pagination || {});
  const totalPages = Math.ceil((pagination.total || 0) / pageSize);
  const hiringStatusesRaw = useSelector((state) => state.setting.hiringStatuses || []);
  const hiringStatuses = useMemo(
    () => hiringStatusesRaw
      .filter((s) => s.is_active !== false)
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)),
    [hiringStatusesRaw]
  );

  useEffect(() => {
    dispatch(getHiringStatuses());
  }, [dispatch]);

  // ── Snack helpers ──────────────────────────────────────────────────────────
  const showSnack = (message, severity = "error") => {
    setSnackMessage(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };
  const snackClosed = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackOpen(false);
  };

  // ── Session persistence ────────────────────────────────────────────────────
  useEffect(() => { sessionStorage.setItem(SESSION_PAGE_SIZE_KEY, pageSize); }, [pageSize, SESSION_PAGE_SIZE_KEY]);
  useEffect(() => { sessionStorage.setItem(SESSION_CURRENT_PAGE_KEY, currentPage); }, [currentPage, SESSION_CURRENT_PAGE_KEY]);
  useEffect(() => { sessionStorage.setItem(SESSION_SEARCH_TERM_KEY, searchTerm); }, [searchTerm, SESSION_SEARCH_TERM_KEY]);

  // ── Click-outside handlers ─────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (
        exportDropdownRef.current && !exportDropdownRef.current.contains(e.target) &&
        (!exportDropdownPortalRef.current || !exportDropdownPortalRef.current.contains(e.target))
      ) setShowExportDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Fetch job details ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!jobId) return;
    if (!latestJob || latestJob.id !== jobId) {
      setJobLoading(true);
      dispatch(fetchJobById(jobId)).then(() => setJobLoading(false));
    }
  }, [jobId, latestJob, dispatch]);

  // ── Clear stale candidates immediately when job changes ───────────────────
  useEffect(() => {
    dispatch(clearCandidateList());
  }, [jobId, dispatch]);

  // ── Load filter config on mount ────────────────────────────────────────────
  useEffect(() => {
    if (!jobId) return;
    (async () => {
      const result = await dispatch(fetchAdvancedFilterConfig(jobId));
      if (result?.success) {
        setFilterItems((result.items || [])
          .filter((item) => item.key !== "qualify_score")
          .map((item) =>
            item.key === "stage_scores" ? { ...item, label: "Stage Score" } : item
          ));
        setSortOptions(result.sortOptions || []);
      }
      setFilterConfigLoaded(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // ── Fetch candidates when config ready or pagination/trigger changes ───────
  useEffect(() => {
    if (!filterConfigLoaded) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfigLoaded, currentPage, pageSize, fetchTrigger]);

  // ── Sort ───────────────────────────────────────────────────────────────────
  const handleSortChange = useCallback((field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
    setFetchTrigger((t) => t + 1);
  }, []);

  const handleSkillsModeChange = useCallback((mode) => {
    setSkillsMatchMode(mode);
    setFiltersChanged(true);
  }, []);

  // ── Build filter payload ───────────────────────────────────────────────────
  const buildFilterPayload = useCallback((fv = filterValues, st = searchTerm) => {
    const payload = {};
    // Sort: sortField may be "hiring_stage_score:stageId" for stage-specific sort
    if (sortField && sortField.includes(":")) {
      const [field, stageId] = sortField.split(":");
      payload.order_by = field;
      payload.sort_hiring_stage_id = stageId;
    } else {
      payload.order_by = sortField || "created_at";
    }
    payload.order_direction = sortDirection || "desc";
    if (st && st.length >= 3) payload.search = st;
    filterItems
      .filter((item) => item.control_type === "checkbox_list")
      .forEach((item) => {
        const vals = fv[item.key];
        if (!vals?.length) return;
        if (item.key === "dynamic_voice_fit") {
          payload.dynamic_voice_fit = [{ fit_labels: vals }];
          return;
        }
        payload[CHECKBOX_LIST_PAYLOAD_KEY_MAP[item.key] || item.key] = vals;
      });
    if (fv.skills?.length) {
      payload.skills = fv.skills;
      payload.skills_match_mode = skillsMatchMode || "all";
    }
    const rs = fv.resume_score;
    if (rs?.min > 0) payload.resume_score_min = rs.min;
    if (rs?.max < 100) payload.resume_score_max = rs.max;
    const expBounds = subitemsData.experience?.config || {};
    const exp = fv.experience;
    if (exp?.min > (expBounds.min ?? 0)) payload.experience_min = exp.min;
    if (exp?.max < (expBounds.max ?? 15)) payload.experience_max = exp.max;
    const ss = fv.stage_scores || {};
    const stageFilters = Object.entries(ss)
      .filter(([, v]) => v.min_percent > 0 || (v.max_percent != null && v.max_percent < 100) || v.status_ids?.length > 0)
      .map(([stageId, v]) => ({
        hiring_stage_id: stageId,
        min_percent: v.min_percent || 0,
        max_percent: v.max_percent ?? 100,
        status_ids: v.status_ids || [],
      }));
    if (stageFilters.length) payload.stage_scores = stageFilters;
    const sts = fv.stage_statuses || {};
    const stageStatusFilters = Object.entries(sts)
      .filter(([, v]) => v.status_ids?.length > 0)
      .map(([stageId, v]) => {
        const ids = v.status_ids || [];
        const includeNoStage = ids.includes("NO_STAGE");
        const filtered = { hiring_stage_id: stageId, status_ids: ids.filter((id) => id !== "NO_STAGE") };
        if (includeNoStage) filtered.include_no_stage = true;
        return filtered;
      });
    if (stageStatusFilters.length) payload.stage_statuses = stageStatusFilters;
    const cr = fv.candidate_responses || {};
    const responses = Object.entries(cr)
      .filter(([, v]) =>
        v.value != null || v.text_contains || v.numeric_min != null || v.numeric_max != null ||
        v.values?.length || v.text_contains_values?.length
      )
      .map(([qId, v]) => {
        const r = { question_id: qId };
        if (v.value != null) r.value = v.value;
        if (v.text_contains) r.text_contains = v.text_contains;
        if (v.numeric_min != null) r.numeric_min = v.numeric_min;
        if (v.numeric_max != null) r.numeric_max = v.numeric_max;
        if (v.values?.length) r.values = v.values;
        if (v.text_contains_values?.length) r.text_contains_values = v.text_contains_values;
        return r;
      });
    if (responses.length) payload.candidate_responses = responses;
    return payload;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection, skillsMatchMode, filterValues, searchTerm, subitemsData, filterItems]);

  // ── Fetch candidates ───────────────────────────────────────────────────────
  const fetchData = async (pageOverride) => {
    if (!jobId) return;
    dispatch(clearCandidateList());
    const page = pageOverride !== undefined ? pageOverride : currentPage;
    const payload = { page, page_size: pageSize, ...buildFilterPayload() };
    setLoading(true);
    await dispatch(searchCandidatesAdvanced(jobId, payload));
    setLoading(false);
  };

  const triggerFetch = useCallback(() => {
    if (currentPage !== 1) setCurrentPage(1);
    else setFetchTrigger((t) => t + 1);
  }, [currentPage]);

  // ── Load accordion subitems ────────────────────────────────────────────────
  const loadSubitemsForKey = useCallback(async (key) => {
    if (subitemsLoadingRef.current[key]) return;
    subitemsLoadingRef.current[key] = true;
    setLoadingAccordion((prev) => ({ ...prev, [key]: true }));
    const result = await dispatch(fetchFilterSubitems(jobId, key, { limit: 50, offset: 0 }));
    subitemsLoadingRef.current[key] = false;
    if (result?.success && result.data) {
      setSubitemsData((prev) => ({ ...prev, [key]: result.data }));
    }
    setLoadingAccordion((prev) => ({ ...prev, [key]: false }));
  }, [dispatch, jobId]);

  const toggleAccordion = useCallback(async (key, enabled) => {
    if (!enabled) return;
    setOpenAccordions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); return next; }
      next.add(key);
      return next;
    });
    if (!subitemsData[key] && !subitemsLoadingRef.current[key]) {
      await loadSubitemsForKey(key);
    }
  }, [subitemsData, loadSubitemsForKey]);

  // Show handed-over filters in the sidebar: open their accordions and load their options
  // (so the boxes appear ticked and the chips show labels instead of ids).
  useEffect(() => {
    const handed = handedFiltersRef.current;
    if (!filterConfigLoaded || !handed) return;
    handedFiltersRef.current = null;
    const keys = Object.keys(handed).filter((k) => filterItems.some((i) => i.key === k && i.enabled !== false));
    if (!keys.length) return;
    setOpenAccordions((prev) => new Set([...prev, ...keys]));
    keys.forEach((k) => loadSubitemsForKey(k));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfigLoaded]);

  const toggleAllAccordions = useCallback(async () => {
    const enabledKeys = filterItems.filter((i) => i.enabled !== false).map((i) => i.key);
    const allOpen = enabledKeys.length > 0 && enabledKeys.every((k) => openAccordions.has(k));
    if (allOpen) {
      setOpenAccordions(new Set());
      return;
    }
    setOpenAccordions(new Set(enabledKeys));
    for (const key of enabledKeys) {
      if (!subitemsData[key] && !subitemsLoadingRef.current[key]) {
        await loadSubitemsForKey(key);
      }
    }
  }, [filterItems, openAccordions, subitemsData, loadSubitemsForKey]);

  // ── Facet search within a filter ───────────────────────────────────────────
  const handleSubitemSearch = useCallback(async (filterKey, term) => {
    setSubitemsSearchTerms((prev) => ({ ...prev, [filterKey]: term }));
    const result = await dispatch(fetchFilterSubitems(jobId, filterKey, {
      limit: 50,
      offset: 0,
      search: term || undefined,
    }));
    if (result?.success && result.data) {
      setSubitemsData((prev) => ({ ...prev, [filterKey]: result.data }));
    }
  }, [dispatch, jobId]);

  // ── Load more subitems ─────────────────────────────────────────────────────
  const handleSubitemLoadMore = useCallback(async (filterKey) => {
    const currentOptions = subitemsData[filterKey]?.options || [];
    const searchTerm = subitemsSearchTerms[filterKey] || "";
    setLoadingMoreMap((prev) => ({ ...prev, [filterKey]: true }));
    try {
      const result = await dispatch(fetchFilterSubitems(jobId, filterKey, {
        limit: 50,
        offset: currentOptions.length,
        search: searchTerm || undefined,
      }));
      if (result?.success && result.data) {
        const newOptions = result.data.options || [];
        const merged = [...currentOptions, ...newOptions];
        const total = result.data.config?.total;
        setSubitemsData((prev) => ({
          ...prev,
          [filterKey]: {
            ...result.data,
            options: merged,
            config: {
              ...result.data.config,
              has_more: total != null ? merged.length < total : result.data.config?.has_more,
            },
          },
        }));
      }
    } finally {
      setLoadingMoreMap((prev) => ({ ...prev, [filterKey]: false }));
    }
  }, [dispatch, jobId, subitemsData, subitemsSearchTerms]);

  // ── Search ─────────────────────────────────────────────────────────────────
  const searchDebounceRef = useRef(null);
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setFiltersChanged(true), 400);
  };

  // ── Filter value updaters ──────────────────────────────────────────────────
  const handleCheckbox = (filterKey, val, checked) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterKey]: checked
        ? [...(prev[filterKey] || []), val]
        : (prev[filterKey] || []).filter((v) => v !== val),
    }));
    setFiltersChanged(true);
  };

  const handlePill = (filterKey, val) => {
    setFilterValues((prev) => {
      const curr = prev[filterKey] || [];
      return { ...prev, [filterKey]: curr.includes(val) ? curr.filter((v) => v !== val) : [...curr, val] };
    });
    setFiltersChanged(true);
  };

  const handleRange = (filterKey, min, max) => {
    setFilterValues((prev) => ({ ...prev, [filterKey]: { min, max } }));
    setFiltersChanged(true);
  };

  const handleStageScore = (stageId, field, value) => {
    setFilterValues((prev) => ({
      ...prev,
      stage_scores: {
        ...prev.stage_scores,
        [stageId]: { ...(prev.stage_scores?.[stageId] || { min_percent: 0, status_ids: [] }), [field]: value },
      },
    }));
    setFiltersChanged(true);
  };

  const handleStageStatus = (stageId, statusIds) => {
    setFilterValues((prev) => ({
      ...prev,
      stage_statuses: {
        ...prev.stage_statuses,
        [stageId]: { status_ids: statusIds },
      },
    }));
    setFiltersChanged(true);
  };

  const handleResponse = (questionId, updates) => {
    setFilterValues((prev) => ({
      ...prev,
      candidate_responses: {
        ...prev.candidate_responses,
        [questionId]: { ...(prev.candidate_responses?.[questionId] || {}), ...updates },
      },
    }));
    setFiltersChanged(true);
  };

  // ── Apply / Clear filters ──────────────────────────────────────────────────
  const handleApplyFilters = () => {
    setFiltersChanged(false);
    triggerFetch();
  };

  const handleClearAllFilters = () => {
    setSearchTerm("");
    setFilterValues({});
    setFiltersChanged(false);
    setCurrentPage(1);
    setFetchTrigger((t) => t + 1);
  };

  // ── Active filter chips ────────────────────────────────────────────────────
  const activeFilterChips = useMemo(() => {
    const chips = [];

    if (searchTerm) {
      chips.push({ key: "search", label: `"${searchTerm}"`, category: "Search" });
    }

    const getFilterLabel = (filterKey) => {
      const item = filterItems.find((i) => i.key === filterKey);
      return item?.label || filterKey;
    };

    const getOptionLabel = (filterKey, val) => {
      const data = subitemsData[filterKey];
      const opt = data?.options?.find((o) => String(o.value ?? o.id ?? "") === val);
      if (opt?.label) return opt.label;
      if (filterKey === "hiring_status") return HIRING_STATUS_ID_TO_LABEL[val] || val;
      return val;
    };

    filterItems
      .filter((item) => item.control_type === "checkbox_list" || item.control_type === "pill_list")
      .forEach((item) => {
        const filterKey = item.key;
        const vals = filterValues[filterKey] || [];
        if (!vals.length) return;
        const labels = vals.map((val) => getOptionLabel(filterKey, val));
        chips.push({ key: filterKey, label: labels.join(", "), category: getFilterLabel(filterKey) });
      });

    const rs = filterValues.resume_score;
    if (rs && (rs.min > 0 || rs.max < 100)) {
      chips.push({ key: "resume_score", label: `${rs.min} – ${rs.max}`, category: "Resume Score" });
    }

    const exp = filterValues.experience;
    const expBounds = subitemsData.experience?.config || {};
    if (exp && (exp.min > (expBounds.min ?? 0) || exp.max < (expBounds.max ?? 15))) {
      chips.push({ key: "experience", label: `${exp.min} – ${exp.max} yrs`, category: "Experience" });
    }

    const ss = filterValues.stage_scores || {};
    const stageData = subitemsData.stage_scores;
    const activeStageEntries = Object.entries(ss).filter(
      ([, v]) => v.min_percent > 0 || (v.max_percent != null && v.max_percent < 100) || v.status_ids?.length > 0
    );
    if (activeStageEntries.length > 0) {
      const parts = activeStageEntries.map(([stageId, sv]) => {
        const stage = stageData?.options?.find((o) => String(o.id ?? "") === stageId);
        const name = stage?.label || stageId.slice(0, 8);
        const detail = [];
        const minP = sv.min_percent ?? 0;
        const maxP = sv.max_percent ?? 100;
        if (minP > 0 || maxP < 100) detail.push(`${minP}–${maxP}%`);
        if (sv.status_ids?.length) detail.push(`${sv.status_ids.length} status`);
        return `${name}${detail.length ? ` (${detail.join(", ")})` : ""}`;
      });
      chips.push({ key: "stage_scores", label: parts.join(" · "), category: "Stage Scores" });
    }

    const sts = filterValues.stage_statuses || {};
    const statusData = subitemsData.stage_statuses;
    const activeStatusEntries = Object.entries(sts).filter(([, v]) => v.status_ids?.length > 0);
    if (activeStatusEntries.length > 0) {
      const parts = activeStatusEntries.map(([stageId, sv]) => {
        const stage = statusData?.options?.find((o) => String(o.id ?? "") === stageId);
        const name = stage?.label || stageId.slice(0, 8);
        return `${name} (${sv.status_ids.length} status)`;
      });
      chips.push({ key: "stage_statuses", label: parts.join(" · "), category: "Stage Status" });
    }

    const cr = filterValues.candidate_responses || {};
    const activeResponses = Object.entries(cr).filter(([, v]) =>
      v.value != null || v.text_contains || v.numeric_min != null || v.numeric_max != null ||
      v.values?.length || v.text_contains_values?.length
    );
    if (activeResponses.length > 0) {
      const qData = subitemsData.candidate_responses;
      const parts = activeResponses.map(([qId]) => {
        const q = qData?.config?.questions?.find((qq) => qq.question_id === qId);
        return q?.label || qId.slice(0, 10);
      });
      chips.push({ key: "candidate_responses", label: parts.join(", "), category: "Responses" });
    }

    return chips;
  }, [searchTerm, filterValues, subitemsData, filterItems]);

  const removeChip = (key) => {
    if (key === "search") {
      setSearchTerm("");
      setFiltersChanged(true);
    } else if (key === "resume_score") {
      setFilterValues((prev) => ({ ...prev, resume_score: { min: 0, max: 100 } }));
      setFiltersChanged(true);
    } else if (key === "experience") {
      const bounds = subitemsData.experience?.config || {};
      setFilterValues((prev) => ({ ...prev, experience: { min: bounds.min ?? 0, max: bounds.max ?? 15 } }));
      setFiltersChanged(true);
    } else if (key === "stage_scores") {
      setFilterValues((prev) => ({ ...prev, stage_scores: {} }));
      setFiltersChanged(true);
    } else if (key === "stage_statuses") {
      setFilterValues((prev) => ({ ...prev, stage_statuses: {} }));
      setFiltersChanged(true);
    } else if (key === "candidate_responses") {
      setFilterValues((prev) => ({ ...prev, candidate_responses: {} }));
      setFiltersChanged(true);
    } else {
      setFilterValues((prev) => ({ ...prev, [key]: [] }));
      setFiltersChanged(true);
    }
  };

  // ── Export ─────────────────────────────────────────────────────────────────
  const handleExport = async (format = "xlsx") => {
    setExportLoading(true);
    setShowExportDropdown(false);
    const payload = { page: 1, page_size: Math.min(pagination.total || 100, 10000), ...buildFilterPayload() };
    const result = await dispatch(exportCandidatesAdvanced(jobId, payload, format));
    if (result?.success && result.blob instanceof Blob) {
      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Candidates_${jobId}.${format}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else {
      showSnack(result?.message || "Failed to export candidates.");
    }
    setExportLoading(false);
  };

  // ── Save search ────────────────────────────────────────────────────────────
  const handleOpenSaveSearch = () => setSaveSearchModal({ open: true, name: "" });

  const handleConfirmSaveSearch = async () => {
    const name = saveSearchModal.name.trim();
    if (!name) { showSnack("Enter a name for this search"); return; }
    const payload = {
      name,
      summary: activeFilterChips.map((c) => c.label).slice(0, 4).join(", ") || "All candidates",
      search_payload: { page: currentPage, page_size: pageSize, ...buildFilterPayload() },
      filter_state: { searchTerm, filterValues, sortField, sortDirection, skillsMatchMode, pageSize },
    };
    const result = await dispatch(createSavedSearch(jobId, payload));
    if (result?.success) {
      showSnack("Search saved successfully", "success");
      setSaveSearchModal({ open: false, name: "" });
    } else {
      showSnack(result?.message || "Failed to save search");
    }
  };

  // ── Saved searches modal ───────────────────────────────────────────────────
  const handleOpenSavedSearches = async () => {
    setSavedSearchesModal({ open: true, list: [], listLoading: true });
    const result = await dispatch(fetchSavedSearches(jobId));
    setSavedSearchesModal({ open: true, list: result?.data || [], listLoading: false });
  };

  const handleLoadSavedSearch = async (item) => {
    setSavedSearchesModal((prev) => ({ ...prev, open: false }));
    const state = item.filter_state;
    if (state) {
      if (state.searchTerm !== undefined) setSearchTerm(state.searchTerm);
      if (state.filterValues) setFilterValues(state.filterValues);
      if (state.sortField) setSortField(state.sortField);
      if (state.sortDirection) setSortDirection(state.sortDirection);
      if (state.skillsMatchMode) setSkillsMatchMode(state.skillsMatchMode);
      if (state.pageSize) setPageSize(state.pageSize);
    }
    setFiltersChanged(false);
    triggerFetch();
    showSnack(`Loaded: ${item.name}`, "success");
  };

  const handleDeleteSavedSearch = async (id) => {
    const result = await dispatch(deleteSavedSearch(jobId, id));
    if (result?.success) {
      setSavedSearchesModal((prev) => ({ ...prev, list: prev.list.filter((s) => s.id !== id) }));
    } else {
      showSnack("Failed to delete saved search");
    }
  };

  // ── Upload handlers ────────────────────────────────────────────────────────
  const MAX_FILES = 20;

  const handleCsvUpload = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const allowedExt = [".csv", ".xls", ".xlsx"];
    const ext = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();
    if (!allowedExt.includes(ext)) { showSnack("Only CSV or Excel files are allowed.", "error"); e.target.value = null; return; }
    if (pdfInputRef.current) pdfInputRef.current.value = null;
    setFile({ type: "csv", data: [f] });
  };

  const handlePdfUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (files.length > MAX_FILES) { showSnack("You can upload a maximum of 20 files.", "error"); e.target.value = null; return; }
    const allowedExt = [".pdf", ".docx"];
    const invalid = files.find((f) => !allowedExt.includes(f.name.substring(f.name.lastIndexOf(".")).toLowerCase()));
    if (invalid) { showSnack("Only PDF or DOCX files are allowed.", "error"); e.target.value = null; return; }
    if (csvInputRef.current) csvInputRef.current.value = null;
    setFile({ type: "pdf", data: files });
  };

  const resetUploadModal = () => {
    setShowUploadModal(false);
    setFile({ type: "", data: [] });
    if (csvInputRef.current) csvInputRef.current.value = null;
    if (pdfInputRef.current) pdfInputRef.current.value = null;
  };

  const handleCandidateUpload = async () => {
    if (!file.data || file.data.length === 0) { showSnack("Please upload candidate file(s)."); return; }
    setUploading(true);
    const totalFiles = file.data.length;
    setUploadProgress({ current: 0, total: totalFiles });
    try {
      if (file.type === "pdf") {
        for (let i = 0; i < totalFiles; i++) {
          setUploadProgress({ current: i + 1, total: totalFiles });
          const result = await dispatch(uploadPdfCandidateFile(jobId, [file.data[i]]));
          if (!result?.success) { showSnack(`Failed at file ${i + 1}: ${result.message}`, "error"); break; }
        }
        showSnack("PDF files uploaded successfully", "success");
      } else if (file.type === "csv") {
        setUploadProgress({ current: 1, total: 1 });
        const result = await dispatch(uploadCsvCandidateFile(jobId, file.data[0]));
        if (result?.success) showSnack(result.message, "success");
        else showSnack(result.message || "CSV upload failed", "error");
      } else {
        showSnack("Only CSV and PDF files are supported."); return;
      }
      setShowUploadModal(false);
      setFile({ type: "", data: [] });
      triggerFetch();
    } catch (err) {
      showSnack(err?.message || "Unexpected error occurred", "error");
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const downloadSampleCsv = () => {
    const headers = ["name", "email", "phonenumber"];
    const dataUri = "data:text/csv;charset=utf-8," + encodeURIComponent(headers.join(","));
    const link = document.createElement("a");
    link.setAttribute("href", dataUri);
    link.setAttribute("download", "sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Edit candidate ─────────────────────────────────────────────────────────
  const handleEditCandidate = async () => {
    const res = await dispatch(updateCandidateDetails(jobId, editModal.candidate.id, editForm));
    if (res?.success) {
      setEditModal({ open: false, candidate: null });
      triggerFetch();
      showSnack("Candidate updated successfully", "success");
    } else {
      showSnack("Failed to update candidate");
    }
  };

  // ── Resume upload ──────────────────────────────────────────────────────────
  const handleResumeUpload = async () => {
    if (!file || !file.name) { showSnack("Please upload a resume."); return; }
    setUploading(true);
    const result = await dispatch(uploadSingleCandidateResume(jobId, resumeModal.candidateId, file));
    setUploading(false);
    if (result.success) {
      showSnack(result.message, "success");
      setResumeModal({ open: false, candidateId: null });
      setFile(null);
    } else {
      showSnack(result.message);
    }
  };

  // ── Selection ──────────────────────────────────────────────────────────────
  const handleSelectJob = (id) => setSelectedJobs((prev) => prev.includes(id) ? prev.filter((j) => j !== id) : [...prev, id]);
  const handleSelectAll = (e) => setSelectedJobs(e.target.checked ? candidateList.map((c) => c.id) : []);

  // ── Status change ──────────────────────────────────────────────────────────
  // status: { id, name } — from the dynamically-fetched hiringStatuses list.
  const handleStatusChange = async (candidateId, status, comment) => {
    const statusId = status?.id;
    const statusName = status?.name;
    if (!statusId) return showSnack("Invalid hiring status");
    const fromName = candidateList.find((c) => c.id === candidateId)?.hiring_status?.name || "In Progress";
    setStatusLoading((prev) => ({ ...prev, [candidateId]: true }));
    const res = await dispatch(updateCandidateDetails(jobId, candidateId, { hiring_status_id: statusId }));
    if (res?.success) {
      if (comment && comment.trim()) {
        const noteText = `Hiring status changed from "${fromName}" to "${statusName}".\n\n${comment.trim()}`;
        await dispatch(postCandidateComment(jobId, candidateId, noteText, []));
      }
      await dispatch(fetchCandidateProfile(jobId, candidateId));
      dispatch(fetchJobById(jobId));
      showSnack(`Status updated to ${statusName} successfully`, "success");
    } else {
      showSnack(`Failed to update status to ${statusName}`);
    }
    setStatusLoading((prev) => { const s = { ...prev }; delete s[candidateId]; return s; });
  };

  const handleBulkStatusChange = async (statusName) => {
    if (!selectedJobs?.length) { showSnack("Please select candidate from List"); return; }
    const status = hiringStatuses.find((s) => s.name === statusName);
    if (!status) { showSnack("Invalid status"); return; }
    setLoading(true);
    const res = await dispatch(bulkUpdateHiringStatus(jobId, { candidate_ids: selectedJobs, hiring_status_id: status.id }));
    if (res?.success) {
      showSnack(`Updated candidates to ${statusName}`, "success");
      setSelectedJobs([]);
      triggerFetch();
    } else {
      showSnack(res?.message || `Failed to update ${statusName}`);
    }
    setLoading(false);
  };

  // ── Stage assign ───────────────────────────────────────────────────────────
  const openApplyStageModal = () => {
    if (selectedJobs?.length) setIsOpen(true);
    else showSnack("Please select candidate from List");
  };

  const selectAssignedStage = async (stageid, videoDateTime, timezone, videoInterviewType) => {
    const total = selectedJobs?.length;
    setAssignProgress({ current: 0, total });
    for (let i = 0; i < total; i++) {
      const isHybrid = videoInterviewType === "hybrid";
      const obj = [{
        job_hiring_stage_id: stageid,
        job_candidate_id: selectedJobs[i],
        ...(!isHybrid && {
          schedule_date: new Date().toISOString(),
          expire_at: new Date(videoDateTime).toISOString(),
          valid_till: new Date(videoDateTime).toISOString(),
          current_timezone: timezone,
          schedule_timezone: timezone,
        }),
        ...(videoInterviewType && { video_interview_type: videoInterviewType }),
      }];
      try {
        const status = await dispatch(assignedStage(candidateList[0]?.job_id, obj));
        if (status !== 200) showSnack(`Failed to assign stage for candidate ${i + 1}`);
      } catch { showSnack(`Error assigning stage to candidate ${i + 1}`); }
      setAssignProgress((prev) => ({ ...prev, current: i + 1 }));
    }
    triggerFetch();
    setSelectedJobs([]);
    setIsOpen(false);
    setAssignProgress({ current: 0, total: 0 });
    showSnack("All candidates assigned stage successfully", "success");
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getDate()} ${date.toLocaleString("default", { month: "short" })} ${date.getFullYear()}`;
  };

  const getDaysFromToday = (dateString) => {
    if (!dateString) return null;
    const targetDate = new Date(dateString);
    const today = new Date();
    targetDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const diffTime = targetDate - today;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today";
    if (diffDays > 0) return `${diffDays} days from now`;
    return `${Math.abs(diffDays)} days ago`;
  };

  const getCandidateStageScore = (candidate, stageId) => {
    const stages = candidate?.hiring_stages?.filter(
      (s) => s.hiring_stage_id === stageId
    );
    if (!stages?.length) return null;
    const latest = stages.reduce((a, b) => new Date(a.created_at) > new Date(b.created_at) ? a : b);
    return { score: latest.score, status: latest.status?.name };
  };

  const currentJobs = candidateList.map((c) => ({
    id: c.id,
    candidates: c.name || "--",
    addedOn: c.created_at ? formatDate(c.created_at) : "--",
    createdAt: c.created_at || null,
    resumeScore: c.match_score !== null && c.match_score !== undefined ? c.match_score : null,
    phone_number: c?.phone_number,
    job_id: c?.job_id,
  }));

  const isDisabled = loading || uploading || jobLoading || exportLoading;

  // ── Sidebar accordion count badges ─────────────────────────────────────────
  const chipCountByKey = useMemo(() => {
    const counts = {};
    if (searchTerm) counts.search = 1;
    filterItems
      .filter((item) => item.control_type === "checkbox_list" || item.control_type === "pill_list")
      .forEach((item) => {
        const len = (filterValues[item.key] || []).length;
        if (len > 0) counts[item.key] = len;
      });
    const rs = filterValues.resume_score;
    if (rs && (rs.min > 0 || rs.max < 100)) counts.resume_score = 1;
    const exp = filterValues.experience;
    const expBounds = subitemsData.experience?.config || {};
    if (exp && (exp.min > (expBounds.min ?? 0) || exp.max < (expBounds.max ?? 15))) counts.experience = 1;
    const activeStages = Object.values(filterValues.stage_scores || {})
      .filter((v) => v.min_percent > 0 || v.status_ids?.length > 0).length;
    if (activeStages > 0) counts.stage_scores = activeStages;
    const activeStatuses = Object.values(filterValues.stage_statuses || {})
      .filter((v) => v.status_ids?.length > 0).length;
    if (activeStatuses > 0) counts.stage_statuses = activeStatuses;
    const activeResponses = Object.values(filterValues.candidate_responses || {})
      .filter((v) =>
        v.value != null || v.text_contains || v.numeric_min != null || v.numeric_max != null ||
        v.values?.length || v.text_contains_values?.length
      ).length;
    if (activeResponses > 0) counts.candidate_responses = activeResponses;
    return counts;
  }, [filterValues, subitemsData, searchTerm, filterItems]);

  return {
    // Identifiers
    jobId,
    // State
    searchTerm, selectedJobs, setSelectedJobs,
    file, setFile,
    snackOpen, snackSeverity, snackMessage,
    uploading, loading, jobLoading, exportLoading, statusLoading,
    uploadProgress, assignProgress,
    editModal, setEditModal,
    editForm, setEditForm,
    resumeModal, setResumeModal,
    showUploadModal, setShowUploadModal,
    isOpen, setIsOpen,
    sortField, sortDirection, sortOptions,
    skillsMatchMode, setSkillsMatchMode, handleSkillsModeChange,
    showExportDropdown, setShowExportDropdown,
    pageSize, setPageSize,
    currentPage, setCurrentPage,
    filterItems, openAccordions, subitemsData, loadingAccordion,
    loadingMoreMap, subitemsSearchTerms,
    filterValues, filterConfigLoaded,
    filtersChanged,
    saveSearchModal, setSaveSearchModal,
    savedSearchesModal, setSavedSearchesModal,
    // Refs
    csvInputRef, pdfInputRef, resumeInputRef,
    exportDropdownRef, exportDropdownPortalRef,
    // Redux
    latestJob, candidateList, pagination, totalPages,
    hiringStatuses,
    // Derived
    currentJobs, isDisabled, activeFilterChips, chipCountByKey,
    // Handlers
    handleSearch, handleSortChange, triggerFetch, toggleAccordion, toggleAllAccordions,
    handleCheckbox, handlePill, handleRange, handleStageScore, handleStageStatus, handleResponse,
    handleApplyFilters, handleClearAllFilters, removeChip,
    handleSubitemSearch, handleSubitemLoadMore,
    handleExport,
    handleOpenSaveSearch, handleConfirmSaveSearch,
    handleOpenSavedSearches, handleLoadSavedSearch, handleDeleteSavedSearch,
    handleCsvUpload, handlePdfUpload, resetUploadModal, handleCandidateUpload, downloadSampleCsv,
    handleResumeUpload, handleEditCandidate,
    handleSelectJob, handleSelectAll,
    showSnack, snackClosed,
    handleStatusChange, handleBulkStatusChange,
    openApplyStageModal, selectAssignedStage,
    getCandidateStageScore, getDaysFromToday,
  };
};

export default useCandidatesV2;
